import { useCallback, useEffect, useRef, useState } from 'react'
import type { BuildEvent, BuildMode, ProjectDto } from '@doable/shared-types'
import {
  ChevronDown,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  Download,
  ExternalLink,
  GitBranch,
  Loader2,
  Monitor,
  Tablet,
  Smartphone,
  MonitorSmartphone,
  Play,
  RefreshCw,
  Rocket,
  RotateCcw,
  Info,
  Send,
  Trash2,
} from 'lucide-react'
import { api, type ApiError, type HetznerStatusDto, type ProjectWithDraft } from '../lib/api'
import { Markdown } from '../components/Markdown'
import { useAuthStore, type LanguageMode } from '../lib/auth-store'
import { navigate } from '../lib/router'
import { cn } from '../lib/cn'
import { FileTree } from '../components/FileTree'
import { Timeline } from '../components/Timeline'
import { Deployments } from '../components/Deployments'
import { Domains } from '../components/Domains'
import { Comments } from '../components/Comments'
import { AppShell } from '../components/shell/AppShell'
import { PageHeader } from '../components/shell/PageHeader'
import { Badge, StatusBox, type BadgeVariant } from '../components/ui/badge'
import { Button, ButtonLink } from '../components/ui/button'
import { Dialog } from '../components/ui/dialog'
import { Textarea } from '../components/ui/input'
import { SegmentedTabs } from '../components/ui/segmented-tabs'
import { EmptyState } from '../components/ui/empty-state'

const WS_BASE = import.meta.env.VITE_WS_URL ?? 'ws://localhost:3000'

type Message =
  | { id: number; role: 'user'; text: string; at: string }
  | { id: number; role: 'system'; text: string; at: string; count?: number }
  | {
      id: number
      role: 'assistant'
      lines: { kind: string; text: string }[]
      status: 'building' | 'ready' | 'live' | 'failed'
      previewUrl?: string
      at: string
      // The mode this turn was actually run in. Used to surface a
      // "Run this plan in Work mode" CTA on the last completed
      // plan-mode message — see runPlanInWorkMode.
      mode?: BuildMode
      // The prompt that produced this turn. Pinned here so the CTA
      // can re-run the same prompt without us having to walk back
      // through messages to find it.
      prompt?: string
    }

type Props = { projectId: string }

type LeftTab = 'chat' | 'files' | 'timeline' | 'deployments' | 'domains' | 'comments'

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  draft: 'outline',
  building: 'volt',
  ready: 'success',
  live: 'success',
  failed: 'destructive',
  archived: 'secondary',
}

/** Flat panel (card DNA) that owns its own height; the chat + preview columns. */
const PANEL =
  'flex min-h-[520px] flex-col overflow-hidden rounded-xl bg-card text-sm text-card-foreground ring-1 ring-foreground/10 h-[70vh] lg:h-[calc(100svh-15rem)]'

/** A failed build whose reason says Anthropic rejected the workspace key. */
function isApiKeyRejection(text: string): boolean {
  return /API key is invalid|\b401\b|authentication_error|Failed to authenticate|Anthropic rejected/i.test(text)
}

export function ProjectDetailPage({ projectId }: Props) {
  const accessToken = useAuthStore((s) => s.accessToken)
  const clear = useAuthStore((s) => s.clear)
  const user = useAuthStore((s) => s.user)
  const tenantRole = useAuthStore((s) => s.tenant?.role)
  const isAdmin = tenantRole === 'owner' || tenantRole === 'admin'
  const languageMode = useAuthStore((s) => s.languageMode)
  const setLanguageMode = useAuthStore((s) => s.setLanguageMode)

  const [project, setProject] = useState<ProjectWithDraft | null>(null)
  // Preview panel selection: null = automatic (draft when there are unpublished changes, else live).
  const [previewMode, setPreviewMode] = useState<'draft' | 'live' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [composer, setComposer] = useState('')
  const [building, setBuilding] = useState(false)
  const [previewKey, setPreviewKey] = useState(0)
  // Preview warm-up (dev server started ahead of the first Send).
  const [warming, setWarming] = useState(false)
  // Device width for the preview frame; remembered per browser.
  type PreviewDevice = 'desktop' | 'tablet' | 'mobile'
  const [device, setDevice] = useState<PreviewDevice>(() => {
    try {
      const v = localStorage.getItem('doable-preview-device')
      return v === 'tablet' || v === 'mobile' ? v : 'desktop'
    } catch {
      return 'desktop'
    }
  })
  const setDeviceAndRemember = (d: PreviewDevice) => {
    setDevice(d)
    try { localStorage.setItem('doable-preview-device', d) } catch { /* private mode */ }
  }
  // Last known draft URL, readable from WS handlers without a stale closure:
  // the iframe only reloads when a draft appears for the FIRST time. Later
  // draft turns are hot-reloaded by the dev server itself.
  const draftUrlRef = useRef<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [leftTab, setLeftTab] = useState<'chat' | 'files' | 'timeline' | 'deployments' | 'domains' | 'comments'>('chat')
  const [confirmReset, setConfirmReset] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [hetznerStatus, setHetznerStatus] = useState<HetznerStatusDto | null>(null)
  // Plan vs Work mode (per spec): plan-first for the very first turn,
  // work after that. Sticky once set so the user can override and have
  // it persist for the rest of the session. Resolved from build history
  // on initial load — null before that to avoid sending the wrong mode
  // on a stale render.
  const [mode, setMode] = useState<BuildMode | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const idRef = useRef(0)
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  // Single-fire guard for the auto-kickoff first build after import. Without
  // this, react would re-trigger on every messages-change render once the
  // build starts streaming back.
  const firstBuildKickedRef = useRef(false)

  const [totalCost, setTotalCost] = useState(0)

  // ── load project + build history ────────────────────────────────────────
  useEffect(() => {
    if (!accessToken) {
      navigate('/login')
      return
    }
    Promise.all([
      api.getProject(accessToken, projectId),
      api.listBuilds(accessToken, projectId),
    ])
      .then(([p, { builds }]) => {
        setProject(p)
        draftUrlRef.current = p.draftUrl ?? null

        // Warm the draft dev server now so the first Send has no cold start.
        // Fire-and-forget; a project that was never built answers 404.
        setWarming(true)
        api.warmPreview(accessToken, projectId).catch(() => {}).finally(() => setWarming(false))

        // Hydrate the chat from the build history. Each build becomes a
        // user message (the prompt) + an assistant message (the outcome).
        const restored: Message[] = []
        let cost = 0
        for (const b of builds.reverse()) {
          restored.push({
            id: ++idRef.current,
            role: 'user',
            text: b.prompt,
            at: b.startedAt,
          })
          // 'ready' = a finished edit-only turn whose result is on the draft
          // preview. Mapping it to 'building' left a permanent spinner on
          // every restored draft turn.
          const status =
            b.status === 'live' ? 'live' : b.status === 'ready' ? 'ready' : b.status === 'failed' ? 'failed' : 'building'
          const lines: { kind: string; text: string }[] = []
          if (b.durationMs) {
            const costStr = b.costUsd ? ` ($${parseFloat(b.costUsd).toFixed(4)})` : ''
            lines.push({
              kind: 'completed',
              text:
                b.status === 'ready'
                  ? `Draft updated (${(b.durationMs / 1000).toFixed(1)}s)${costStr}`
                  : b.status === 'live'
                    ? `Published (${(b.durationMs / 1000).toFixed(1)}s)${costStr}`
                    : `Built in ${(b.durationMs / 1000).toFixed(1)}s${costStr}`,
            })
          }
          if (b.status === 'failed') {
            lines.push({ kind: 'failed', text: b.failureReason ?? 'Build failed' })
          }
          restored.push({
            id: ++idRef.current,
            role: 'assistant',
            lines,
            status: status as 'building' | 'ready' | 'live' | 'failed',
            previewUrl: b.previewUrl ?? undefined,
            at: b.finishedAt ?? b.startedAt,
            prompt: b.prompt,
          })
          if (b.costUsd) cost += parseFloat(b.costUsd)
        }

        // If no builds yet, just seed with the initial prompt.
        if (restored.length === 0 && p.initialPrompt) {
          restored.push({
            id: ++idRef.current,
            role: 'user',
            text: p.initialPrompt,
            at: p.createdAt,
          })
        }

        setMessages(restored)
        setTotalCost(cost)

        // Resolve the default mode: if no successful builds, this is the
        // very first turn → Plan. Otherwise → Work. The user can override
        // via the toggle and the choice sticks for the session.
        const hasCompleted = builds.some((b) => b.status === 'live' || b.status === 'ready')
        setMode(hasCompleted ? 'work' : 'plan')
      })
      .catch((err: ApiError) => {
        if (err.status === 401) {
          clear()
          navigate('/login')
          return
        }
        setError(err.title ?? 'Failed to load project')
      })
  }, [accessToken, projectId, clear])

  // ── poll Hetzner staging status ─────────────────────────────────────────
  // Project create fires staging provisioning in the background; we poll
  // here so the UI knows when the workspace is ready. Slow cadence once
  // running (cost is just an existence check); fast while creating.
  useEffect(() => {
    if (!accessToken) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null

    async function tick() {
      try {
        const status = await api.hetzner.status(accessToken!)
        if (cancelled) return
        setHetznerStatus(status)
        const next =
          status.stagingStatus === 'creating' ? 5_000
          : status.stagingStatus === 'error' ? 30_000
          : 60_000
        timer = setTimeout(tick, next)
      } catch {
        if (!cancelled) timer = setTimeout(tick, 30_000)
      }
    }
    tick()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [accessToken])

  // ── auto-scroll ──────────────────────────────────────────────────────────
  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages])

  // System notices ("workspace reset", "approved plan") collapse when the
  // same notice repeats back to back, instead of stacking five identical lines.
  const appendSystem = useCallback((text: string) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1]
      if (last && last.role === 'system' && last.text === text) {
        return [...prev.slice(0, -1), { ...last, count: (last.count ?? 1) + 1, at: new Date().toISOString() }]
      }
      return [...prev, { id: ++idRef.current, role: 'system', text, at: new Date().toISOString() }]
    })
  }, [])

  const appendUser = useCallback((text: string) => {
    const msg: Message = { id: ++idRef.current, role: 'user', text, at: new Date().toISOString() }
    setMessages((prev) => [...prev, msg])
    return msg.id
  }, [])

  const startAssistantMessage = useCallback(
    (init: { mode?: BuildMode; prompt?: string } = {}) => {
      const id = ++idRef.current
      const msg: Message = {
        id,
        role: 'assistant',
        lines: [],
        status: 'building',
        at: new Date().toISOString(),
        ...(init.mode && { mode: init.mode }),
        ...(init.prompt && { prompt: init.prompt }),
      }
      setMessages((prev) => [...prev, msg])
      return id
    },
    [],
  )

  const updateAssistant = useCallback(
    (id: number, mutator: (m: Extract<Message, { role: 'assistant' }>) => void) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== id || m.role !== 'assistant') return m
          const copy: Extract<Message, { role: 'assistant' }> = {
            ...m,
            lines: [...m.lines],
          }
          mutator(copy)
          return copy
        }),
      )
    },
    [],
  )

  // ── send a build (initial or iteration) ──────────────────────────────────
  const sendBuild = useCallback(
    (prompt: string | undefined, deploy = false, overrideMode?: BuildMode) => {
      if (!accessToken || !project) return
      if (building) return

      // overrideMode wins — used by "Run this plan in Work mode" so we
      // don't race the React state update for `mode`.
      const effectiveMode: BuildMode | null = overrideMode ?? mode
      // Sticky update so subsequent manual sends keep Work mode after
      // the user explicitly approved the plan.
      if (overrideMode && overrideMode !== mode) setMode(overrideMode)

      setBuilding(true)
      const assistantId = startAssistantMessage({
        ...(effectiveMode && { mode: effectiveMode }),
        ...(prompt && { prompt }),
      })

      const url = `${WS_BASE}/v1/projects/${project.id}/build?token=${encodeURIComponent(accessToken)}`
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            type: 'build',
            ...(prompt ? { prompt } : {}),
            deploy,
            ...(effectiveMode ? { mode: effectiveMode } : {}),
          }),
        )
      }

      ws.onmessage = (raw) => {
        let event: BuildEvent
        try {
          event = JSON.parse(raw.data) as BuildEvent
        } catch {
          return
        }
        switch (event.type) {
          case 'started':
            updateAssistant(assistantId, (m) => {
              m.lines.push({ kind: 'started', text: 'Build started' })
            })
            break
          case 'step':
            updateAssistant(assistantId, (m) => {
              m.lines.push({ kind: 'step', text: event.label })
            })
            break
          case 'log':
            updateAssistant(assistantId, (m) => {
              m.lines.push({ kind: 'log', text: event.message })
            })
            break
          case 'status':
            updateAssistant(assistantId, (m) => {
              m.status = event.status
            })
            break
          case 'completed': {
            // Webflow-style delivery: an edit-only turn (deploy=false) lands
            // on the DRAFT preview (hot-reloading dev server) and never
            // touches the live URL; only Publish (deploy=true) goes live.
            const secs = (event.durationMs / 1000).toFixed(1)
            const isDraft = !deploy
            const newStatus = isDraft ? 'ready' : 'live'
            updateAssistant(assistantId, (m) => {
              m.status = newStatus as 'building' | 'live' | 'failed'
              m.previewUrl = event.previewUrl || undefined
              m.lines.push({
                kind: 'completed',
                text: isDraft
                  ? event.previewUrl
                    ? `Draft updated (${secs}s)`
                    : `Changes saved (${secs}s). Publish to make them live.`
                  : `Published to ${event.previewUrl} (${secs}s)`,
              })
            })
            setProject((p) =>
              p
                ? {
                    ...p,
                    status: newStatus,
                    ...(isDraft
                      ? { draftUrl: event.previewUrl || p.draftUrl || null, draftUpdatedAt: event.at, hasUnpublishedChanges: true }
                      : { ...(event.previewUrl ? { previewUrl: event.previewUrl } : {}), lastBuiltAt: event.at, hasUnpublishedChanges: false }),
                    initialPrompt: prompt ?? p.initialPrompt,
                  }
                : p,
            )
            setPreviewMode(isDraft ? 'draft' : 'live')
            if (isDraft) {
              // The dev server hot-reloaded the frame while Claude worked; a
              // forced reload here only caused a blank flash. Reload once when
              // the draft URL appears for the first time.
              const firstDraft = !draftUrlRef.current && !!event.previewUrl
              if (event.previewUrl) draftUrlRef.current = event.previewUrl
              if (firstDraft) setPreviewKey((k) => k + 1)
            }
            // Track cost from log events. The translator emits "$X.XXXX" in the result line.
            // We already parsed cost server-side; a simpler approach here: the cost shows up
            // in the log events. Just add a rough parser.
            {
              const costLine = messages
                .find((m) => m.role === 'assistant' && m.id === assistantId)
              if (costLine && costLine.role === 'assistant') {
                const match = costLine.lines.find((l) => l.text.includes('$'))?.text.match(/\$([0-9.]+)/)
                if (match) setTotalCost((c) => c + parseFloat(match[1]!))
              }
            }
            // Publish: the live site changed underneath the frame — refetch.
            if (!isDraft) setPreviewKey((k) => k + 1)
            break
          }
          case 'failed':
            updateAssistant(assistantId, (m) => {
              m.status = 'failed'
              m.lines.push({ kind: 'failed', text: event.reason })
            })
            setProject((p) => (p ? { ...p, status: 'failed' } : p))
            break
        }
      }

      ws.onerror = () => {
        updateAssistant(assistantId, (m) => {
          m.status = 'failed'
          m.lines.push({ kind: 'failed', text: 'Connection error' })
        })
      }

      ws.onclose = () => {
        setBuilding(false)
        wsRef.current = null
      }
    },
    [accessToken, project, building, mode, startAssistantMessage, updateAssistant],
  )

  // Auto-kickoff first build for freshly-imported projects (status='draft',
  // no completed builds yet). Mirrors the Lovable UX: arrive on a project
  // and the agent is already starting to read the codebase. Single-fire
  // guarded by firstBuildKickedRef.
  //
  // Gated on staging readiness: when Hetzner is connected we wait for the
  // staging server to be 'running' before kicking off the build, otherwise
  // the worker would just fail at the deploy step. If Hetzner isn't
  // connected at all, we let the build run anyway — it'll succeed locally
  // (no preview URL); the user sees a "Connect Hetzner to deploy" CTA.
  useEffect(() => {
    if (firstBuildKickedRef.current) return
    if (!project || !accessToken) return
    if (project.status !== 'draft') return
    if (building) return
    if (!project.initialPrompt) return
    if (messages.length !== 1 || messages[0]?.role !== 'user') return
    if (hetznerStatus?.connected && hetznerStatus.stagingStatus !== 'running') return
    if (mode === null) return // wait for mode resolution from build history
    firstBuildKickedRef.current = true
    sendBuild(project.initialPrompt, false)
  }, [project, accessToken, building, messages, sendBuild, hetznerStatus, mode])

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    const text = composer.trim()
    if (!text || building) return
    setComposer('')
    appendUser(text)
    sendBuild(text, false) // edit-only — fast, no deploy
  }

  // Closes the Plan→Work loop the partner spec describes: when a plan
  // turn finished and the user approves, we re-run the same prompt in
  // Work mode without forcing them to copy/paste it. The override
  // is sticky so further manual sends stay in Work too.
  const runPlanInWorkMode = useCallback(
    (prompt: string) => {
      if (!prompt || building) return
      appendSystem('Approved plan — running in Work mode')
      sendBuild(prompt, false, 'work')
    },
    [building, sendBuild],
  )

  function onDeploy() {
    if (!accessToken || !project || building) return
    setBuilding(true)
    const assistantId = startAssistantMessage()

    const url = `${WS_BASE}/v1/projects/${project.id}/build?token=${encodeURIComponent(accessToken)}`
    const ws = new WebSocket(url)
    wsRef.current = ws

    ws.onopen = () => {
      // Deploy-only: skip Claude, just rsync + build + deploy from existing workspace.
      ws.send(JSON.stringify({ type: 'deploy' }))
    }

    // Reuse the same event handling as sendBuild.
    ws.onmessage = (raw) => {
      let event: BuildEvent
      try { event = JSON.parse(raw.data) as BuildEvent } catch { return }
      switch (event.type) {
        case 'started':
          updateAssistant(assistantId, (m) => { m.lines.push({ kind: 'started', text: 'Deploying…' }) })
          break
        case 'step':
          updateAssistant(assistantId, (m) => { m.lines.push({ kind: 'step', text: event.label }) })
          break
        case 'log':
          updateAssistant(assistantId, (m) => { m.lines.push({ kind: 'log', text: event.message }) })
          break
        case 'status':
          updateAssistant(assistantId, (m) => { m.status = event.status })
          break
        case 'completed': {
          const isReady = !event.previewUrl
          updateAssistant(assistantId, (m) => {
            m.status = isReady ? 'ready' : 'live'
            m.previewUrl = event.previewUrl || undefined
            m.lines.push({ kind: 'completed', text: `Published to ${event.previewUrl || 'the live site'} (${(event.durationMs / 1000).toFixed(1)}s)` })
          })
          setProject((p) => p ? { ...p, status: 'live', previewUrl: event.previewUrl || p.previewUrl, lastBuiltAt: event.at, hasUnpublishedChanges: false } : p)
          setPreviewMode('live')
          setPreviewKey((k) => k + 1)
          break
        }
        case 'failed':
          updateAssistant(assistantId, (m) => { m.status = 'failed'; m.lines.push({ kind: 'failed', text: `Deploy failed: ${event.reason}` }) })
          setProject((p) => p ? { ...p, status: 'failed' } : p)
          break
      }
    }
    ws.onerror = () => { updateAssistant(assistantId, (m) => { m.status = 'failed'; m.lines.push({ kind: 'failed', text: 'Connection error' }) }) }
    ws.onclose = () => { setBuilding(false); wsRef.current = null }
  }

  function onRollback(snapshotId: string) {
    if (!accessToken || !project || building) return
    if (!window.confirm('Roll the live deployment back to this snapshot? Your chat history stays untouched — only the live URL reverts.')) {
      return
    }
    setBuilding(true)
    const assistantId = startAssistantMessage()
    appendUser(`Rollback to snapshot ${snapshotId.slice(0, 8)}…`)

    const url = `${WS_BASE}/v1/projects/${project.id}/build?token=${encodeURIComponent(accessToken)}`
    const ws = new WebSocket(url)
    wsRef.current = ws

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'rollback', snapshotId }))
    }

    // Reuse onDeploy's event handling shape — rollback is a deploy variant.
    ws.onmessage = (raw) => {
      let event: BuildEvent
      try { event = JSON.parse(raw.data) as BuildEvent } catch { return }
      switch (event.type) {
        case 'started':
          updateAssistant(assistantId, (m) => { m.lines.push({ kind: 'started', text: 'Rolling back…' }) })
          break
        case 'step':
          updateAssistant(assistantId, (m) => { m.lines.push({ kind: 'step', text: event.label }) })
          break
        case 'log':
          updateAssistant(assistantId, (m) => { m.lines.push({ kind: 'log', text: event.message }) })
          break
        case 'status':
          updateAssistant(assistantId, (m) => { m.status = event.status })
          break
        case 'completed': {
          updateAssistant(assistantId, (m) => {
            m.status = event.previewUrl ? 'live' : 'ready'
            m.previewUrl = event.previewUrl || undefined
            m.lines.push({ kind: 'completed', text: `Rolled back (${(event.durationMs / 1000).toFixed(1)}s)` })
          })
          setProject((p) => p ? { ...p, status: 'live', previewUrl: event.previewUrl || p.previewUrl, lastBuiltAt: event.at, hasUnpublishedChanges: false } : p)
          setPreviewKey((k) => k + 1)
          break
        }
        case 'failed':
          updateAssistant(assistantId, (m) => { m.status = 'failed'; m.lines.push({ kind: 'failed', text: `Rollback failed: ${event.reason}` }) })
          setProject((p) => p ? { ...p, status: 'failed' } : p)
          break
      }
    }
    ws.onerror = () => { updateAssistant(assistantId, (m) => { m.status = 'failed'; m.lines.push({ kind: 'failed', text: 'Connection error' }) }) }
    ws.onclose = () => { setBuilding(false); wsRef.current = null }
  }

  async function onDelete() {
    if (!accessToken || !project) return
    try {
      await api.deleteProject(accessToken, project.id)
      navigate('/dashboard')
    } catch (err) {
      setError((err as ApiError).title ?? 'Delete failed')
      setConfirmDelete(false)
    }
  }

  async function onReset() {
    if (!accessToken || !project) return
    setResetting(true)
    try {
      await api.resetProject(accessToken, project.id)
      setProject((p) =>
        p ? { ...p, status: 'draft', previewUrl: null, lastBuiltAt: null } : p,
      )
      appendSystem('Workspace reset — the next build starts from a fresh template')
      setPreviewKey((k) => k + 1)
    } catch (err) {
      setError((err as ApiError).title ?? 'Reset failed')
    } finally {
      setResetting(false)
      setConfirmReset(false)
    }
  }

  const crumbs = [
    { label: 'Projects', href: '/dashboard' },
    { label: project?.name ?? 'Project' },
  ]

  if (!accessToken) return null
  if (!project && !error) {
    return (
      <AppShell crumbs={crumbs}>
        <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" /> Loading project…
        </div>
      </AppShell>
    )
  }
  if (!project) {
    return (
      <AppShell crumbs={crumbs}>
        <EmptyState
          icon={<CircleAlert />}
          title="Could not load this project"
          body={error ?? 'Something went wrong.'}
          action={<Button variant="outline" onClick={() => navigate('/dashboard')}>Back to projects</Button>}
        />
      </AppShell>
    )
  }

  // Surface staging-server lifecycle as a non-blocking banner. We
  // intentionally don't gate the whole page — the user can still chat,
  // browse files, etc. — only the auto-kickoff first build waits.
  const stagingBanner = (() => {
    if (!hetznerStatus?.connected) return null
    if (hetznerStatus.stagingStatus === 'creating') {
      return {
        tone: 'info' as const,
        text: 'Setting up your workspace on Hetzner — this usually takes about a minute. Your first preview will go live as soon as the server is ready.',
      }
    }
    if (hetznerStatus.stagingStatus === 'error') {
      return {
        tone: 'error' as const,
        text: 'Workspace setup failed. Open Settings → Hetzner to retry, or contact support if it keeps happening.',
      }
    }
    return null
  })()

  // The last build failed because Anthropic rejected the workspace key →
  // point the user at Settings instead of letting them retry blindly.
  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant')
  const apiKeyRejected =
    lastAssistant?.role === 'assistant' &&
    lastAssistant.status === 'failed' &&
    lastAssistant.lines.some((l) => l.kind === 'failed' && isApiKeyRejection(l.text))

  const tabItems: Array<{ value: LeftTab; label: string }> = [
    { value: 'chat', label: 'Chat' },
    { value: 'files', label: 'Files' },
    { value: 'timeline', label: 'Timeline' },
    { value: 'deployments', label: 'Deploys' },
    { value: 'domains', label: 'Domains' },
    { value: 'comments', label: 'Comments' },
  ]

  // Draft / live delivery state. Draft = the hot-reloading dev preview a Send
  // produces; live = what Publish deployed. The API computes these from the
  // build history; this session's events keep them current in between.
  const draftUrl = project.draftUrl ?? null
  if (draftUrl && !draftUrlRef.current) draftUrlRef.current = draftUrl
  const liveUrl = project.previewUrl ?? null
  const hasUnpublished = !!project.hasUnpublishedChanges
  const previewSelection: 'draft' | 'live' | null =
    previewMode && ((previewMode === 'draft' && draftUrl) || (previewMode === 'live' && liveUrl))
      ? previewMode
      : draftUrl && (hasUnpublished || !liveUrl)
        ? 'draft'
        : liveUrl
          ? 'live'
          : null
  const activePreviewUrl = previewSelection === 'draft' ? draftUrl : previewSelection === 'live' ? liveUrl : null

  return (
    <AppShell crumbs={crumbs}>
      <PageHeader
        eyebrow="Project"
        title={
          <>
            <span className="truncate">{project.name}</span>
            <Badge variant={STATUS_VARIANT[project.status] ?? 'outline'} className="uppercase tracking-wide">
              {project.status}
            </Badge>
            {hasUnpublished ? (
              <Badge variant="warning" title={project.draftUpdatedAt ? `Changes since ${new Date(project.draftUpdatedAt).toLocaleString()}` : 'Draft changes not published yet'}>
                Draft changes not live
              </Badge>
            ) : project.status === 'live' && project.previewUrl ? (
              <Badge variant="success" title={project.lastBuiltAt ? `Published ${new Date(project.lastBuiltAt).toLocaleString()}` : undefined}>
                Live · up to date
              </Badge>
            ) : null}
            {totalCost > 0 && (
              <Badge variant="secondary" className="tabular-nums" title="Total Anthropic spend for this project">
                ${totalCost.toFixed(2)}
              </Badge>
            )}
          </>
        }
        description={
          <span className="font-mono text-xs">
            {project.stack} · /{project.slug}
          </span>
        }
        actions={
          <>
            {project.previewUrl && (
              <ButtonLink variant="outline" size="sm" href={project.previewUrl} target="_blank" rel="noreferrer">
                <ExternalLink /> Open
              </ButtonLink>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (!accessToken || !project || exporting) return
                setExporting(true)
                try {
                  const res = await api.exportToGitlab(accessToken, project.id)
                  window.open(res.repoUrl, '_blank')
                } catch (err) {
                  setError((err as ApiError).title ?? 'Export failed')
                } finally {
                  setExporting(false)
                }
              }}
              disabled={exporting || building}
            >
              <GitBranch /> {exporting ? 'Pushing…' : 'GitLab'}
            </Button>
            <ButtonLink
              variant="outline"
              size="sm"
              href={`${(import.meta.env.VITE_API_URL ?? 'http://localhost:3000')}/v1/projects/${project.id}/download?token=${accessToken}`}
              target="_blank"
              rel="noreferrer"
            >
              <Download /> ZIP
            </ButtonLink>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmReset(true)}
              disabled={building || resetting}
              title="Wipe the workspace and the live deployment, keep the project + prompt"
            >
              <RotateCcw /> {resetting ? 'Resetting…' : 'Reset'}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setConfirmDelete(true)}
              disabled={building || resetting}
            >
              <Trash2 /> Delete
            </Button>
            <Button
              size="sm"
              variant={hasUnpublished || (!project.previewUrl && !!draftUrl) ? 'default' : 'outline'}
              onClick={onDeploy}
              disabled={building || resetting}
              title={hasUnpublished ? 'Build and publish the draft to the live URL' : 'Nothing new to publish — republish anyway'}
            >
              <Rocket /> {building ? 'Publishing…' : 'Publish'}
            </Button>
          </>
        }
      />

      {stagingBanner && (
        <StatusBox tone={stagingBanner.tone === 'error' ? 'destructive' : 'volt'} className="flex items-center gap-3">
          {stagingBanner.tone === 'info' && <Loader2 aria-hidden className="size-4 shrink-0 animate-spin" />}
          <span>{stagingBanner.text}</span>
        </StatusBox>
      )}

      {error && (
        <StatusBox tone="destructive" className="flex items-center justify-between gap-3">
          <span>{error}</span>
          <Button variant="ghost" size="xs" onClick={() => setError(null)}>Dismiss</Button>
        </StatusBox>
      )}

      {/* Body: left panel (chat/files) | preview */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,27rem)_minmax(0,1fr)] xl:grid-cols-[minmax(0,30rem)_minmax(0,1fr)]">
        <section className={PANEL}>
          {/* Tab headers */}
          <div className="flex shrink-0 items-center border-b px-3 py-2.5">
            <SegmentedTabs size="sm" value={leftTab} onChange={setLeftTab} items={tabItems} className="w-full justify-between" />
          </div>

          {/* Files tab */}
          {leftTab === 'files' && accessToken && (
            <FileTree projectId={project.id} accessToken={accessToken} refreshKey={previewKey} />
          )}

          {/* Timeline tab */}
          {leftTab === 'timeline' && accessToken && (
            <Timeline projectId={project.id} accessToken={accessToken} refreshKey={previewKey} />
          )}

          {/* Deployments tab */}
          {leftTab === 'deployments' && accessToken && (
            <Deployments
              projectId={project.id}
              accessToken={accessToken}
              refreshKey={previewKey}
              onRollback={onRollback}
            />
          )}

          {/* Domains tab */}
          {leftTab === 'domains' && accessToken && (
            <Domains
              projectId={project.id}
              accessToken={accessToken}
              isAdmin={isAdmin}
              refreshKey={previewKey}
            />
          )}

          {/* Comments tab — read-shaped to all members; canPost is
              commenter+ (which excludes plain viewers). canDeleteAny
              is admin+; authors can always delete their own. */}
          {leftTab === 'comments' && accessToken && (
            <Comments
              projectId={project.id}
              accessToken={accessToken}
              currentUserId={user?.id}
              canPost={
                tenantRole === 'owner' ||
                tenantRole === 'admin' ||
                tenantRole === 'editor' ||
                tenantRole === 'commenter'
              }
              canDeleteAny={isAdmin}
            />
          )}

          {/* Chat tab */}
          {leftTab === 'chat' && (
          <>
          <div ref={scrollerRef} className="min-h-0 flex-1 space-y-2.5 overflow-y-auto p-3">
            {apiKeyRejected && (
              <StatusBox tone="destructive" className="flex flex-col gap-2">
                <span>
                  Your Anthropic API key was rejected by Anthropic. Update it under Settings → Anthropic.
                </span>
                <Button variant="outline" size="xs" className="self-start" onClick={() => navigate('/settings')}>
                  Open Settings
                </Button>
              </StatusBox>
            )}
            {messages.length === 0 && (
              <EmptyState
                icon={<Send />}
                title="Start building"
                body="Type what you want below. The first turn runs in Plan mode, so Claude outlines the approach before touching files."
              />
            )}
            {messages.map((m, i) => (
              <ChatMessage
                key={m.id}
                message={m}
                languageMode={languageMode}
                isLast={i === messages.length - 1}
                onRunPlanInWorkMode={runPlanInWorkMode}
                onRetry={(prompt, m) => sendBuild(prompt, false, m)}
                onOpenSettings={() => navigate('/settings')}
                buildingNow={building}
              />
            ))}
          </div>

          {hasUnpublished && !building && (
            <StatusBox tone="warning" className="mx-3 mb-2 flex items-center justify-between gap-3 py-2">
              <span className="text-xs">Your latest changes are on the draft preview. Publish to make them live.</span>
              <Button size="xs" onClick={onDeploy} disabled={building || resetting} className="shrink-0"><Rocket /> Publish</Button>
            </StatusBox>
          )}
          <form onSubmit={onSubmit} className="shrink-0 space-y-2 border-t p-3">
            <Textarea
              value={composer}
              onChange={(e) => setComposer(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault()
                  onSubmit(e)
                }
              }}
              placeholder={
                building
                  ? 'Building — wait for the current run to finish'
                  : 'What should change?  (⌘+Enter to send)'
              }
              disabled={building}
              rows={3}
              className="min-h-0 resize-none"
              maxLength={4000}
            />
            {/* Tech Line — spec §10 + §14.4. The persistent strip
                surfacing what would otherwise be hidden context: which
                env the build affects, which model is running, and what
                this session has cost so far. Subtle styling so it
                doesn't compete with the composer. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
              <span>
                Env <strong className="font-medium text-foreground">staging</strong>
              </span>
              <span className="text-muted-foreground/50">·</span>
              <span>
                Model <strong className="font-medium text-foreground">claude-opus-5</strong>
              </span>
              <span className="text-muted-foreground/50">·</span>
              <span className="tabular-nums">
                Session <strong className="font-medium text-foreground">${totalCost.toFixed(2)}</strong>
              </span>
              {project?.previewUrl && (
                <>
                  <span className="text-muted-foreground/50">·</span>
                  <a
                    href={project.previewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    open live <ExternalLink className="size-3" />
                  </a>
                </>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <ModeToggle mode={mode} onChange={setMode} disabled={building} />
                <LanguageToggle mode={languageMode} onChange={setLanguageMode} />
              </div>
              <Button type="submit" size="sm" disabled={building || !composer.trim()}>
                <Send /> {mode === 'plan' ? 'Plan' : 'Send'}
              </Button>
            </div>
          </form>
          </>
          )}
        </section>

        {/* Preview */}
        <section className={PANEL}>
          <div className="flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2">
            <div className="flex min-w-0 items-center gap-2">
              {(draftUrl || liveUrl) && (
                <SegmentedTabs
                  size="sm"
                  className="shrink-0"
                  value={previewSelection ?? 'live'}
                  onChange={(v) => { setPreviewMode(v); setPreviewKey((k) => k + 1) }}
                  items={[
                    ...(draftUrl ? [{ value: 'draft' as const, label: 'Draft' }] : []),
                    ...(liveUrl ? [{ value: 'live' as const, label: 'Live' }] : []),
                  ]}
                />
              )}
              {activePreviewUrl ? (
                <Badge className="shrink-0" variant={previewSelection === 'draft' ? 'warning' : 'success'}>{previewSelection === 'draft' ? 'Draft' : 'Live'}</Badge>
              ) : null}
              {activePreviewUrl ? (
                <p className="min-w-0 truncate font-mono text-xs text-muted-foreground" title={activePreviewUrl}>{activePreviewUrl}</p>
              ) : warming ? (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Loader2 aria-hidden className="size-3.5 animate-spin" /> Preparing live preview…
                </p>
              ) : (
                <p className="truncate font-mono text-xs text-muted-foreground">no preview yet — send your first prompt</p>
              )}
              {building && mode !== 'plan' && previewSelection === 'draft' && (
                <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground" aria-live="polite">
                  <span className="relative flex size-2">
                    <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-60" />
                    <span className="relative inline-flex size-2 rounded-full bg-success" />
                  </span>
                  Updating live…
                </span>
              )}
            </div>
            {activePreviewUrl && (
              <div className="flex shrink-0 items-center gap-1">
                <SegmentedTabs<PreviewDevice>
                  size="sm"
                  value={device}
                  onChange={setDeviceAndRemember}
                  className="mr-1 hidden lg:inline-flex"
                  items={[
                    { value: 'desktop', label: <Monitor aria-label="Desktop" className="size-3.5" /> },
                    { value: 'tablet', label: <Tablet aria-label="Tablet" className="size-3.5" /> },
                    { value: 'mobile', label: <Smartphone aria-label="Mobile" className="size-3.5" /> },
                  ]}
                />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Refresh preview"
                  title="Refresh preview"
                  onClick={() => setPreviewKey((k) => k + 1)}
                >
                  <RefreshCw />
                </Button>
                <ButtonLink
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Open preview in a new tab"
                  title="Open in new tab"
                  href={activePreviewUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExternalLink />
                </ButtonLink>
              </div>
            )}
          </div>
          <div className="flex min-h-0 flex-1 items-stretch justify-center bg-muted/30 p-3">
            {activePreviewUrl ? (
              <div
                className={cn('h-full max-w-full overflow-hidden rounded-xl border bg-card transition-[width] duration-200 ease-out', device === 'desktop' && 'w-full')}
                style={device === 'tablet' ? { width: 820 } : device === 'mobile' ? { width: 390 } : undefined}
              >
                <iframe
                  key={`${previewSelection}-${previewKey}`}
                  src={activePreviewUrl}
                  title={`${project.name} — ${previewSelection === 'draft' ? 'draft preview' : 'live site'}`}
                  className="h-full w-full bg-card"
                />
              </div>
            ) : (
              <div className="flex w-full items-center justify-center">
                <EmptyState
                  className="border-0 bg-transparent"
                  icon={<MonitorSmartphone />}
                  title={building ? 'The agent is working' : project?.status === 'failed' ? 'Last build failed' : 'No preview yet'}
                  body={
                    building
                      ? 'The preview will appear here after the build.'
                      : project?.status === 'failed'
                        ? 'Check the chat for details and send another message to retry.'
                        : 'The preview will appear here after the first successful build. Send a message in the Chat tab to get started.'
                  }
                />
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Reset confirm */}
      <Dialog
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        title="Reset workspace?"
        description="We'll wipe the project files, stop the running container, and remove the nginx vhost. The project itself and your prompt are kept — the next build starts from a fresh template."
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setConfirmReset(false)}>Cancel</Button>
            <Button variant="destructive" size="sm" onClick={onReset}>Reset</Button>
          </>
        }
      />

      {/* Delete confirm */}
      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete this project?"
        description="We'll stop the running container, delete the nginx vhost, remove the source files on the app server, and soft-delete the project in the database. This cannot be undone."
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button variant="destructive" size="sm" onClick={onDelete}>Delete project</Button>
          </>
        }
      />
    </AppShell>
  )
}

function ChatMessage({
  message,
  languageMode,
  isLast,
  onRunPlanInWorkMode,
  onRetry,
  onOpenSettings,
  buildingNow,
}: {
  message: Message
  languageMode: LanguageMode
  isLast: boolean
  onRunPlanInWorkMode: (prompt: string) => void
  onRetry: (prompt: string, mode?: BuildMode) => void
  onOpenSettings: () => void
  buildingNow: boolean
}) {
  const [expanded, setExpanded] = useState(false)

  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-xl bg-muted px-3 py-2 text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-foreground">
          <Markdown text={message.text} />
        </div>
      </div>
    )
  }
  if (message.role === 'system') {
    return (
      <div className="flex items-center gap-3 py-1 text-[11px] text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        <span className="inline-flex items-center gap-1.5">
          <Info aria-hidden className="size-3" />
          {message.text}
          {message.count && message.count > 1 ? <span className="rounded-full bg-muted px-1.5 tabular-nums">×{message.count}</span> : null}
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>
    )
  }

  // assistant — three render modes:
  //   building  → spinner + the most recent informative line
  //   done      → check + summary, with a "show details" disclosure
  //   failed    → warning + first failure line + disclosure
  // The full per-event log lives behind the disclosure so the chat stays scannable.
  const isBuilding = message.status === 'building'
  const isFailed = message.status === 'failed'
  const visibleLines = message.lines.filter(notNoise)

  // Headline pick differs by language mode:
  //   moderate  — prefer step events (already human-shaped: "Building
  //               Docker image", "Rsyncing files"). Skip log lines so we
  //               don't surface "Edit src/components/Foo.tsx" or shell
  //               commands. Fallback message for the gap before the
  //               first step arrives.
  //   technical — current behavior: most recent log OR step.
  const lastInfoLine =
    languageMode === 'moderate'
      ? [...visibleLines].reverse().find((l) => l.kind === 'step')
      : [...visibleLines].reverse().find((l) => l.kind === 'log' || l.kind === 'step')
  const completedLine = visibleLines.find((l) => l.kind === 'completed')
  const failedLine = visibleLines.find((l) => l.kind === 'failed')
  const failureReason = failedLine?.text.replace(/^(Build |Deploy |Rollback )?failed:?\s*/i, '').trim() ?? ''
  const keyRejected = isFailed && isApiKeyRejection(failureReason)

  // Quiet result cards: one neutral surface, the status lives in the icon
  // and headline colour only (no filled green/red blocks in the transcript).
  const tone = isFailed
    ? 'border-destructive/30 bg-card'
    : 'border-border bg-card'
  const headlineColor = isFailed
    ? 'text-destructive'
    : isBuilding
      ? 'text-muted-foreground'
      : 'text-success-ink'

  return (
    <div className="flex justify-start">
      <div className={cn('max-w-[95%] rounded-xl border px-3 py-2 text-xs leading-relaxed', tone)}>
        <div className={cn('flex items-center gap-2 [&_svg]:size-3.5 [&_svg]:shrink-0', headlineColor)}>
          {isBuilding && <Loader2 aria-hidden className="animate-spin" />}
          {!isBuilding && (isFailed ? <CircleAlert aria-hidden /> : <CircleCheck aria-hidden />)}
          <span className="truncate">
            {isBuilding
              ? lastInfoLine?.text ?? 'Working on your changes…'
              : isFailed
                ? 'Build failed'
                : completedLine?.text ?? 'Done'}
          </span>
        </div>

        {!isBuilding && !isFailed && message.status === 'ready' && message.previewUrl && (
          <a
            href={message.previewUrl}
            target="_blank"
            rel="noreferrer"
            className="lp-arrow-link mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-foreground underline-offset-4 hover:underline [&_svg]:size-3"
          >
            View draft <ExternalLink />
          </a>
        )}

        {isFailed && failureReason && !/^build failed$/i.test(failureReason) && (
          <p className="mt-1 whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-foreground">
            {keyRejected
              ? 'Anthropic rejected the workspace API key. Update it under Settings → Anthropic, then retry.'
              : failureReason}
          </p>
        )}

        {isFailed && isLast && !buildingNow && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {keyRejected ? (
              <Button variant="outline" size="xs" onClick={onOpenSettings}>Open Settings</Button>
            ) : null}
            {message.prompt ? (
              <Button variant={keyRejected ? 'ghost' : 'outline'} size="xs" onClick={() => onRetry(message.prompt!, message.mode)}>
                <RotateCcw /> Retry
              </Button>
            ) : null}
          </div>
        )}

        {!isBuilding && visibleLines.length > 1 && (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground [&_svg]:size-3"
          >
            {expanded ? <ChevronDown /> : <ChevronRight />}
            {expanded ? 'hide details' : `${visibleLines.length} steps`}
          </button>
        )}

        {expanded && (
          <div className="mt-1.5 space-y-0.5 border-t pt-1.5 font-mono text-[11px] text-muted-foreground">
            {visibleLines.map((line, i) => (
              <p
                key={i}
                className={
                  line.kind === 'step'
                    ? 'text-foreground'
                    : line.kind === 'failed'
                      ? 'text-destructive'
                      : line.kind === 'completed'
                        ? 'text-success-ink'
                        : 'text-muted-foreground'
                }
              >
                {line.text}
              </p>
            ))}
          </div>
        )}

        {/* Plan→Work CTA — only on the LAST message and only when this
            turn was a plan that ran cleanly. Re-runs the same prompt
            in Work mode and sticks the toggle there for subsequent
            turns. Hidden during builds and on stale (non-last) plan
            messages so we never offer to "execute an old plan". */}
        {isLast &&
          !isBuilding &&
          !isFailed &&
          message.mode === 'plan' &&
          message.prompt &&
          !buildingNow && (
            <Button
              variant="outline"
              size="xs"
              className="mt-2"
              onClick={() => onRunPlanInWorkMode(message.prompt!)}
            >
              <Play /> Run this plan in Work mode
            </Button>
          )}
      </div>
    </div>
  )
}

/**
 * Plan vs Work mode picker. Defaults are decided by the parent (Plan for
 * the very first turn, Work afterwards), but the user can override here
 * and the choice sticks for the rest of the session.
 *
 * Plan mode runs Claude in read-only `permissionMode: 'plan'` so it
 * outputs a plan without touching files. Lives next to Send so the user
 * sees exactly what mode the next turn will run in.
 */
function ModeToggle({
  mode,
  onChange,
  disabled,
}: {
  mode: BuildMode | null
  onChange: (m: BuildMode) => void
  disabled: boolean
}) {
  if (mode === null) {
    return <span className="text-[11px] text-muted-foreground">Loading mode…</span>
  }
  const tip =
    mode === 'plan'
      ? 'Plan mode — Claude outlines what it would do without making changes. Run again in Work mode to execute.'
      : 'Work mode — Claude makes the actual changes to the workspace.'
  return (
    <div title={tip} className={cn(disabled && 'pointer-events-none opacity-50')}>
      <SegmentedTabs<BuildMode>
        size="sm"
        value={mode}
        onChange={onChange}
        items={[
          { value: 'plan', label: 'Plan' },
          { value: 'work', label: 'Work' },
        ]}
      />
    </div>
  )
}

/**
 * Language toggle — Moderate (end-user friendly headlines, technical
 * detail tucked behind "show details") vs Technical (raw log lines as
 * headlines, current pre-W3 behavior). Stored in zustand-persist so
 * the choice survives reloads, scoped per browser. We can promote it
 * to a server-side user preference later if cross-device sync matters.
 */
function LanguageToggle({
  mode,
  onChange,
}: {
  mode: LanguageMode
  onChange: (m: LanguageMode) => void
}) {
  const tip =
    mode === 'moderate'
      ? 'Moderate language — only end-user friendly status. Click Show details for technical info.'
      : 'Technical language — raw tool output and file paths in the headline.'
  return (
    <div title={tip}>
      <SegmentedTabs<LanguageMode>
        size="sm"
        value={mode}
        onChange={onChange}
        items={[
          { value: 'moderate', label: 'Simple' },
          { value: 'technical', label: 'Technical' },
        ]}
      />
    </div>
  )
}

/**
 * Filter out internal-noise lines that the worker emits (workspace summaries,
 * "started" markers, etc.). The user just wants to see what changed.
 */
function notNoise(line: { kind: string; text: string }): boolean {
  if (line.kind === 'started') return false
  if (/^workspace contains \d+ files/.test(line.text)) return false
  if (/^reusing existing workspace/.test(line.text)) return false
  if (/^seeded workspace from/.test(line.text)) return false
  return true
}
